import { useCustom, useDataProvider } from "@refinedev/core";
import { useOrganization } from "hooks/useOrganization";
import { useMemo } from "react";
import { OrganizationSubscription } from "types";
const CUSTOM_PLAN_NAME = "Custom";

export function useGetActiveSubscriptions() {
  const { organization } = useOrganization({});
  const orgId = organization?.id;
  const dataProvider = useDataProvider();

  const url = `api/organizations/${orgId}/subscriptions/active`;
  const method = "get";
  const { data, isLoading, isError, error, refetch } =
    useCustom<OrganizationSubscription>({
      url,
      method,
      queryOptions: {
        enabled: !!orgId,
        staleTime: 1000 * 60 * 5, // 5 minutes
        // the API returns a 404 if the organization has no active subscription
        // it causes an endless loop of refetching in the subscriptions page
        // there is a side effect with the Redirector isLoading state
        queryFn: async () => {
          try {
            return await dataProvider().custom({
              url,
              method,
            });
          } catch {
            // Intentionally swallow 404 errors - API returns 404 when no active subscription exists
            return { data: null };
          }
        },
      },
      errorNotification: false,
    });

  // useful for analytics
  // maybe the mapping should be moved to analytics helpers
  const activeSubscriptionDetails = useMemo(() => {
    if (!data?.data) return null;
    const { subscribed_products, billing_interval, license_type } = data.data;
    const isCustom = license_type === CUSTOM_PLAN_NAME;
    return {
      plan: subscribed_products?.at(-1)?.product_name || "Trial",
      plan_type: subscribed_products?.at(-1)?.product_type,
      billing_interval,
      isCustom,
    };
  }, [data]);

  // False while loading so callers don't briefly flash free-user UI to paying
  // users before the subscription resolves.
  const subscriptions = data?.data;
  const isFreeUser =
    !isLoading && (!subscriptions || subscriptions.license_type === "Free");

  return {
    subscriptions,
    isFreeUser,
    isLoading,
    isError,
    error,
    activeSubscriptionDetails,
    refetch,
  };
}
